import { TranslateService } from '@ngx-translate/core';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { DeviceErrorType, DeviceState } from 'src/app/models/device-manger';
import { DeviceType } from '../devices-handle-util';
import {
  DeviceErrorHelperModelBase,
  DeviceErrorHelperStep,
} from './device-error-helper-model-base';

const NO_INPUT_HELP_ARTICLE_URL =
  'https://intercom.help/pencil/en/articles/6899614-troubleshooting-no-camera-input-issue';

export class NoInputHelper extends DeviceErrorHelperModelBase {
  private _steps: DeviceErrorHelperStep[] = [];

  constructor(
    protected deviceDetector: DeviceAndBrowserDetectorService,
    protected translateService: TranslateService,
    protected deviceType: DeviceType,
  ) {
    super(translateService, deviceDetector, deviceType);
  }

  getSteps(): DeviceErrorHelperStep[] {
    this._steps = [];
    if (this.deviceDetector.isDesktop()) {
      this.desktopSteps();
    } else if (this.deviceDetector.isTabletDevice()) {
      this.tabletSteps();
    } else {
      this.mobileSteps();
    }

    return this._steps;
  }

  getMobileTitle(): string {
    return this.translateService.instant('Resolve "no input detected"');
  }

  getDesktopTitle(): string {
    return this.translateService.instant('Resolve "no input detected"');
  }

  hasRelatedError(cam: DeviceState, mic: DeviceState) {
    return (
      this.hasErrorOfType(cam, DeviceErrorType.NO_INPUT_DETECTED) ||
      this.hasErrorOfType(mic, DeviceErrorType.NO_INPUT_DETECTED)
    );
  }

  showRefreshButton(): boolean {
    return false;
  }

  getErrorMsg(cam: DeviceState, mic: DeviceState): string {
    const camError = this.hasErrorOfType(cam, DeviceErrorType.NO_INPUT_DETECTED);
    const micError = this.hasErrorOfType(mic, DeviceErrorType.NO_INPUT_DETECTED);

    if (camError && micError) {
      return this.translateService.instant('No input detected');
    } else if (camError) {
      return this.translateService.instant('No input from cam');
    } else if (micError) {
      return this.translateService.instant('No input from mic');
    } else {
      return '';
    }
  }

  desktopSteps() {
    this._steps = [
      {
        stepText:
          `${this.translateService.instant('Close any apps that may be using your')} ` +
          `${this.strongHtml(this.translateService.instant('camera'))} ` +
          `${this.translateService.instant('or')} ` +
          `${this.strongHtml(this.translateService.instant('microphone'))}` +
          `${this.translateService.instant(', such as Zoom or Google Meet')}`,
        stepImgSrc: 'helper-modal-guides/noInputDetectedSteps/common/step-1.png',
      },
      {
        stepText:
          `${this.translateService.instant('Try')} ` +
          `${this.strongHtml(this.translateService.instant('reloading'))} ` +
          `${this.translateService.instant('your browser or')} <br />` +
          `${this.strongHtml(this.translateService.instant('restarting'))} ` +
          `${this.translateService.instant('your device.')}`,
        stepImgSrc: 'helper-modal-guides/noInputDetectedSteps/desktop/step-2.png',
      },
      {
        stepText:
          `${this.translateService.instant('If issues persist, review our')} ` +
          `${this.externalHelpLinkHtml(
            NO_INPUT_HELP_ARTICLE_URL,
            this.translateService.instant('help doc'),
          )} ` +
          `${this.translateService.instant('or')} <br />` +
          `${this.strongHtml(this.translateService.instant('contact support'))}`,
      },
    ];
  }

  tabletSteps() {
    this._steps = [
      {
        stepText:
          `${this.translateService.instant('Close any apps that may be using your')} ` +
          `${this.strongHtml(this.translateService.instant('camera'))} ` +
          `${this.translateService.instant('or')} ` +
          `${this.strongHtml(this.translateService.instant('microphone'))}` +
          `${this.translateService.instant(', such as Zoom or Google Meet')}`,
        stepImgSrc: 'helper-modal-guides/noInputDetectedSteps/common/step-1.png',
      },
      {
        stepText:
          `${this.translateService.instant('Try')} ` +
          `${this.strongHtml(this.translateService.instant('reloading'))} ` +
          `${this.translateService.instant('your browser or')} <br />` +
          `${this.strongHtml(this.translateService.instant('restarting'))} ` +
          `${this.translateService.instant('your device.')}`,
        stepImgSrc: 'helper-modal-guides/noInputDetectedSteps/tablet/step-2.png',
      },
      {
        stepText:
          `${this.translateService.instant('If issues persist, review our')} ` +
          `${this.externalHelpLinkHtml(
            NO_INPUT_HELP_ARTICLE_URL,
            this.translateService.instant('help doc'),
          )} ` +
          `${this.translateService.instant('or')} <br />` +
          `${this.strongHtml(this.translateService.instant('contact support'))}`,
      },
    ];
  }

  mobileSteps() {
    this._steps = [
      {
        stepText:
          `${this.translateService.instant('Close any apps that may be using your')} ` +
          `${this.strongHtml(this.translateService.instant('camera'))} ` +
          `${this.translateService.instant('or')} ` +
          `${this.strongHtml(this.translateService.instant('microphone'))}` +
          `${this.translateService.instant(', such as Zoom or Google Meet')}`,
        stepImgSrc: 'helper-modal-guides/noInputDetectedSteps/common/step-1.png',
      },
      {
        stepText:
          `${this.translateService.instant('Try')} ` +
          `${this.strongHtml(this.translateService.instant('reloading'))} ` +
          `${this.translateService.instant('your browser or')} ` +
          `${this.strongHtml(this.translateService.instant('restarting'))} ` +
          `${this.translateService.instant('your device.')}`,
        stepImgSrc: 'helper-modal-guides/noInputDetectedSteps/mobile/step-2.png',
      },
      {
        stepText:
          `${this.translateService.instant('If issues persist, review our')} ` +
          `${this.externalHelpLinkHtml(
            NO_INPUT_HELP_ARTICLE_URL,
            this.translateService.instant('help doc'),
          )} ` +
          `${this.translateService.instant('or')} ${this.strongHtml(
            this.translateService.instant('contact support'),
          )}.`,
      },
    ];
  }
}
